import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, Menu, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { fetchUnreadNotifications } from "../lib/media/notifications";
import { groupNotifications, unreadCountOf } from "../lib/media/notify";
import { useMediaTasks } from "../lib/mediaTasks";
import { useNavItems } from "../lib/navPrefs";
import { NAV_BY_KEY, type NavKey } from "./nav";
import NotificationSheet from "./NotificationSheet";

export interface MenuItem {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
}

/** PC(xl〜)のヘッダーに直接並べる操作。スマホでは画面側が入口(丸ボタン等)を持つ */
export interface HeaderAction {
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  /** 画面の主操作(追加など)。塗りで目立たせる */
  primary?: boolean;
  /** 切り替え式の操作(選択モードなど)の「オン」表示 */
  pressed?: boolean;
  disabled?: boolean;
}

interface Props {
  /** どのタブか。見出しの名前と絵はここから引く(下部ナビと必ず揃う) */
  navKey: NavKey;
  /** その画面だけの項目 */
  menu?: MenuItem[];
  /** PC でだけヘッダーに出す画面の主操作 */
  actions?: HeaderAction[];
  /** 見出しの下に添える小さな一言(ストリーク表示など)。中身の見た目は呼び出し側が持つ */
  subtitle?: ReactNode;
}

/**
 * 各画面の見出しと右上のメニュー。
 *
 * サインアウトはナビ(サイドバー・下部バー)が持つので、ここには置かない。
 */
export default function ScreenHeader({ navKey, menu = [], actions = [], subtitle }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [inbox, setInbox] = useState(false);
  const tasks = useMediaTasks();
  // メニューで表示名やアイコンを変えたら、見出しも同じ姿になる
  const items = useNavItems();
  const { label, icon: Icon } = items.find((item) => item.key === navKey) ?? NAV_BY_KEY[navKey];

  // 30秒ごとに取り直す。相手の操作は自分の画面には届かないので、
  // 気づける程度の間隔で見に行く
  const unreadRows = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: fetchUnreadNotifications,
    refetchInterval: 30_000,
  });
  const unread = useMemo(
    () => unreadCountOf(groupNotifications(unreadRows.data ?? [])),
    [unreadRows.data],
  );

  // 新着が「届いた瞬間」だけベルを一振りする。開いた直後の未読では鳴らさない
  const [ringing, setRinging] = useState(false);
  const prevUnread = useRef<number | null>(null);
  useEffect(() => {
    if (!unreadRows.isSuccess) return;
    if (prevUnread.current !== null && unread > prevUnread.current) {
      setRinging(true);
      const timer = setTimeout(() => {
        setRinging(false);
      }, 700);
      prevUnread.current = unread;
      return () => {
        clearTimeout(timer);
      };
    }
    prevUnread.current = unread;
  }, [unread, unreadRows.isSuccess]);

  // 既読はシートの中で付ける(押した行だけ、またはまとめて)。
  // 閉じたときはバッジを合わせ直すだけ
  async function closeInbox() {
    setInbox(false);
    await queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }

  return (
    <>
      {/* sticky で常に見える見出しにする。ポップアップはこのヘッダーを
          基準に置くので、スクロール位置に関わらずボタンの真下に出る。
          本文(Shell)が PC でも全幅になったため、幅は親に従うだけでよい */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-[var(--color-line)] bg-[var(--color-bg)] p-4 xl:h-16 xl:px-6 xl:py-0">
        <div className="flex min-w-0 flex-col gap-1">
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            {/* まずはここだけテーマカラー。他への展開は指示を待って広げる */}
            <Icon className="size-5 text-[var(--color-accent)]" aria-hidden />
            {label}
          </h1>
          {subtitle}
        </div>
        <div className="flex items-center gap-6">
          {/* PC の主操作(追加・選択など)。スマホは画面側の丸ボタンが受け持つ */}
          {actions.length > 0 && (
            <div className="hidden items-center gap-2 xl:flex">
              {actions.map(({ label, icon: Icon, onSelect, primary, pressed, disabled }) => (
                <button
                  key={label}
                  type="button"
                  onClick={onSelect}
                  disabled={disabled}
                  aria-pressed={pressed}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs disabled:opacity-50 ${
                    primary === true || pressed === true
                      ? "bg-[var(--color-accent)] font-medium text-white"
                      : "border border-[var(--color-line)] bg-white text-[var(--color-ink)]"
                  }`}
                >
                  <Icon className="size-4" aria-hidden />
                  {label}
                </button>
              ))}
            </div>
          )}
          {/* PC では画面の操作をメニューに隠さず、そのまま並べる */}
          {menu.length > 0 && (
            <div className="hidden items-center gap-1 xl:flex">
              {menu.map(({ label, icon: Icon, onSelect }) => (
                <button
                  key={label}
                  type="button"
                  onClick={onSelect}
                  className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-[var(--color-sub)] hover:bg-white"
                >
                  <Icon className="size-4" aria-hidden />
                  {label}
                </button>
              ))}
            </div>
          )}
          {/* PC では「画面ごとの操作|どの画面でも同じ機能(ベル)」の区切りを見せる */}
          {(actions.length > 0 || menu.length > 0) && (
            <span aria-hidden className="hidden h-5 w-px bg-[var(--color-line)] xl:block" />
          )}
          {/* 見た目は20pxのまま、当たり判定だけ44pxに広げる(負のマージンで高さは据え置き) */}
          <button
            type="button"
            onClick={() => {
              setInbox(true);
            }}
            aria-label={unread > 0 ? `通知 ${String(unread)}件` : "通知"}
            className={`relative -m-3 flex size-11 items-center justify-center rounded-full transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] ${
              unread > 0 ? "text-[var(--color-accent)]" : "text-[var(--color-sub)]"
            }`}
          >
            {/* transform を効かせるため inline のままにしない */}
            <span className={`flex ${ringing ? "bell-ring" : ""}`}>
              <Bell className="size-5" aria-hidden />
            </span>
            {unread > 0 && (
              // 白地にテーマカラーの縁と数字。メディア詳細のコメント件数と同じ形。
              // bg-white はダークで card 色に読み替えられるため、ここは白を直に置く
              <span className="absolute top-1.5 right-1.5 flex min-w-4 items-center justify-center rounded-full border-[1.5px] border-[var(--color-accent)] bg-[#fff] px-1 text-[10px] leading-4 font-bold text-[var(--color-accent)]">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
          {/* PC ではボタンが並ぶのでメニュー自体が要らない。項目が無い画面でも出さない */}
          {menu.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setOpen((current) => !current);
              }}
              aria-expanded={open}
              aria-label="メニュー"
              className="-m-3 flex size-11 items-center justify-center rounded-full text-[var(--color-sub)] transition-colors hover:bg-[color-mix(in_srgb,var(--color-ink)_7%,transparent)] xl:hidden"
            >
              <Menu className="size-5" aria-hidden />
            </button>
          )}
        </div>

        {open && (
          <>
            <button
              type="button"
              aria-label="メニューを閉じる"
              onClick={() => {
                setOpen(false);
              }}
              className="fixed inset-0 z-30 cursor-default"
            />
            <div className="absolute top-full right-4 z-40 -mt-1 flex w-max min-w-48 max-w-72 flex-col overflow-hidden rounded-xl border border-[var(--color-line)] bg-white shadow-lg">
              {menu.map(({ label, icon: Icon, onSelect }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onSelect();
                  }}
                  className="flex items-center gap-2 border-b border-[var(--color-line)] px-4 py-3 text-left text-sm last:border-b-0"
                >
                  <Icon className="size-4 text-[var(--color-sub)]" aria-hidden />
                  {label}
                </button>
              ))}
            </div>
          </>
        )}
      </header>

      {/* アップロード・ダウンロード準備の進み具合。処理は画面を移っても走り続ける
          ため、どの画面でもヘッダーの直下に出す */}
      {(tasks.exporting !== null || tasks.upload !== null) && (
        <div className="flex flex-col gap-3 px-4 pt-3">
          {tasks.exporting !== null && (
            <div className="flex flex-col gap-1 text-sm text-[var(--color-sub)]">
              <span className="text-xs">
                ダウンロード準備中... {tasks.exporting.done} / {tasks.exporting.total}
              </span>
              <span className="h-1 overflow-hidden rounded bg-[var(--color-line)]">
                <span
                  className="block h-full bg-[var(--color-accent)] transition-[width]"
                  style={{
                    width: `${String(
                      Math.round((tasks.exporting.done / tasks.exporting.total) * 100),
                    )}%`,
                  }}
                />
              </span>
            </div>
          )}
          {tasks.upload !== null && (
            <div className="flex flex-col gap-1 text-sm text-[var(--color-sub)]">
              <span className="truncate">
                {tasks.upload.done + 1} / {tasks.upload.total}: {tasks.upload.name}
              </span>
              <span className="text-xs">
                {tasks.upload.phase === "preparing"
                  ? "サムネイルを作成中..."
                  : tasks.upload.phase === "saving"
                    ? "保存中..."
                    : `送信中 ${String(Math.round(tasks.upload.ratio * 100))}%`}
              </span>
              <span className="h-1 overflow-hidden rounded bg-[var(--color-line)]">
                <span
                  className="block h-full bg-[var(--color-teal)] transition-[width]"
                  style={{ width: `${String(Math.round(tasks.upload.ratio * 100))}%` }}
                />
              </span>
            </div>
          )}
        </div>
      )}

      {inbox && <NotificationSheet onClose={() => void closeInbox()} />}
    </>
  );
}
